import NextImage, { StaticImageData } from 'next/image';

import { Container } from '../../../components/Container/Container';
import { Heading } from '../../../components/Heading/Heading';
import { Text } from '../../../components/Text/Text';
import { InternalLink } from '../../../components/Clickables/InternalLink/InternalLink';
import { ArrowRightIcon } from '../../../components/Icons/ArrowRightIcon';
import { twMerge } from 'tailwind-merge';
import { AppOnlyPill } from '../../../components/Clickables/Pill/AppOnlyPill';
import { useIntersectionObserver } from '../../../helpers/hooks/useIntersectionObserver';

interface IProps {
  imgSrc: StaticImageData;
  imgAlt: string;
  imgRight?: boolean;
  headingText?: string;
  bodyText?: string;
  readMoreLink?: string;
  children?: React.ReactNode;
  className?: string;
  appOnlyPill?: boolean;
}

export const CircleImageAndText = ({
  imgSrc,
  imgAlt,
  imgRight = false,
  headingText,
  bodyText,
  readMoreLink,
  children,
  className,
  appOnlyPill,
}: IProps) => {
  const [isInView, ref] = useIntersectionObserver({});
  const translateX = imgRight ? 'translateX(100%)' : 'translateX(-100%)';

  return (
    <Container
      className={twMerge(
        `mt-sm flex flex-col items-center md:justify-around ${imgRight ? 'sm:flex-row-reverse' : 'sm:flex-row'}`,
        className,
      )}
    >
      <div ref={ref} className={`sm:w-col-5 md:w-col-4 flex w-full justify-start overflow-hidden`}>
        <NextImage
          style={{
            opacity: isInView ? 1 : 0,
            transform: isInView ? 'translateX(0)' : translateX,
            transition: 'opacity 1s ease-in-out 0.25s, transform 1s ease-in-out 0.25s',
          }}
          alt={imgAlt}
          className="size-full object-cover sm:mb-0"
          src={imgSrc}
          placeholder="blur"
        />
      </div>
      <div className="sm:w-col-7 md:w-col-5 mt-6 sm:pl-4">
        {appOnlyPill && <AppOnlyPill />}
        {headingText && <Heading variant="md">{headingText}</Heading>}
        {bodyText && (
          <Text variant="lg" className="mt-4">
            {bodyText}
          </Text>
        )}
        {children}
        {readMoreLink && (
          <InternalLink
            to={readMoreLink}
            className="font-heading text-pinktail hover:text-pinktail-shade flex items-center py-6 pr-6"
          >
            Read more
            <div className="ml-0.5 inline-block">
              <ArrowRightIcon width={20} height={20} />
            </div>
          </InternalLink>
        )}
      </div>
    </Container>
  );
};
